import {
  buildCoachAvailabilityQuery,
  CoacheeElementTokenProvider,
  CronofyDateTimePicker,
} from '@efdigital/ll-addon-coaching';
import { Heading } from '@efdigital/ll-addon-ui';
import { Spinner } from '@efdigital/ll-xceed-v2/components/application/spinner.gts';
import { CoachProfileHeader } from '@efdigital/ll-xceed-v2/components/coaching/coach/coach-profile-header.gts';
import SessionCard from '@efdigital/ll-xceed-v2/components/coaching/coachee/session-card.gts';
import type { CoachingCoacheeCohortCoachesCoachModel } from '@efdigital/ll-xceed-v2/routes/coaching/coachee/cohort/coaches/coach';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type {
  DateTimePickerAction,
  DateTimePickerConfig,
} from 'cronofy-elements';
import { pageTitle } from 'ember-page-title';

interface BookChemistrySignature {
  Args: {
    model: CoachingCoacheeCohortCoachesCoachModel;
    controller: unknown;
  };
}

const dateTimePickerConfig: DateTimePickerConfig = {
  logs: 'info',
};

export default class BookChemistryTemplate extends Component<BookChemistrySignature> {
  @tracked saving = false;

  dateTimePickerAction = async (action: DateTimePickerAction) => {
    if (action.notification.type === 'slot_selected') {
      const idempotencyKey = crypto.randomUUID();
      this.saving = true;
      try {
        const { start, end } = action.notification.slot;
        await this.args.model.planner.bookSession(
          this.args.model.coach.user,
          'chemistry',
          idempotencyKey,
          start,
          end
        );
      } catch (error) {
        console.error(error);
        if (error instanceof Error) {
          window.alert(error.message);
        }
      } finally {
        this.saving = false;
      }
    }
  };

  <template>
    {{pageTitle "Book chemistry session"}}

    <div class="container mb-3">
      <CoachProfileHeader
        @coach={{@model.coach}}
        @partnershipStatus={{@model.planner.getStatusForCoach
          @model.coach.user
        }}
        class="mb-5"
      />

      {{#if (@model.planner.canBookChemistrySession @model.coach.user)}}
        <Heading
          @size="sm"
          @text="Book chemistry session"
          @secondary="Select a time and date for your chemistry session."
        />
        <div class="card rounded-4 shadow-sm border p-3 p-lg-4">
          <div class="card-body">
            {{#if this.saving}}
              <Spinner />
            {{else}}
              <CoacheeElementTokenProvider
                @coachId={{@model.coach.user}}
                as |cronofyContext|
              >
                {{#if cronofyContext.accountStatus.accountSub}}
                  <CronofyDateTimePicker
                    @cronofyContext={{cronofyContext}}
                    @availabilityQuery={{buildCoachAvailabilityQuery
                      @model.coachingConfig.chemistrySessionLengthMins
                      cronofyContext.accountStatus.accountSub
                    }}
                    @onAction={{this.dateTimePickerAction}}
                    @elementConfig={{dateTimePickerConfig}}
                    class="mb-5 d-flex flex-row justify-content-center"
                  />
                {{else}}
                  <div class="alert alert-danger">
                    <p class="mb-0">You cannot book a chemistry session with
                      this coach because they have not connected their calendar
                      to the system.</p>
                  </div>
                {{/if}}
              </CoacheeElementTokenProvider>
            {{/if}}
          </div>
        </div>
      {{else}}
        {{#let
          (@model.planner.getChemistrySessions @model.coach.user)
          as |existingSessions|
        }}
          {{#if existingSessions.length}}

            <Heading
              @size="sm"
              @text="Chemistry session"
              @secondary="You have booked a chemistry session with this coach."
            />
          {{/if}}
          {{#each existingSessions as |existingSession|}}
            <SessionCard
              @session={{existingSession}}
              @coach={{@model.coach}}
              @coachingConfig={{@model.coachingConfig}}
              @planner={{@model.planner}}
              class="mb-2"
            />
          {{else}}
            <div class="alert alert-danger">
              <p class="mb-0">Sorry, you cannot book a chemistry session with
                this coach.</p>
            </div>
          {{/each}}
        {{/let}}
      {{/if}}
    </div>
  </template>
}
